import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AuthShell, AUTH_INPUT, AUTH_LABEL, AUTH_SUBMIT } from './AuthShell';

export const PasswordResetPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { t } = useTranslation();

  const [form, setForm] = useState({
    email: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });

      if (error) {
        toast({
          title: t('passwordResetFailed'),
          description: error.message,
          variant: 'destructive',
        });
      } else {
        setEmailSent(true);
        toast({
          title: t('resetLinkSent'),
          description: t('checkEmailReset'),
        });
      }
    } catch (error: unknown) {
      console.error('Password reset error:', error);
      toast({
        title: t('passwordResetFailed'),
        description: t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthShell>
        <div className="space-y-5 text-center">
          <div className="space-y-1.5">
            <h2 className="text-lg font-medium text-white">{t('resetLinkSent')}</h2>
            <p className="text-sm leading-relaxed text-white/60">{t('checkEmailReset')}</p>
          </div>
          <Button onClick={() => navigate('/auth')} className={AUTH_SUBMIT}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToLogin')}
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5 text-center">
          <h2 className="text-lg font-medium text-white">{t('resetPassword')}</h2>
          <p className="text-sm leading-relaxed text-white/60">{t('resetPasswordDescription')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className={AUTH_LABEL}>{t('email')}</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email"
            placeholder="user@example.com"
            required
            className={AUTH_INPUT}
          />
        </div>

        <Button type="submit" className={AUTH_SUBMIT} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('sendResetLink')}
        </Button>

        <button
          type="button"
          onClick={() => navigate('/auth')}
          className="flex w-full items-center justify-center text-sm text-white/55 transition-colors hover:text-white"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          {t('backToLogin')}
        </button>
      </form>
    </AuthShell>
  );
};
